import { useState, useEffect, Suspense } from 'react';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { useAppSelector } from '../../redux/hooks';
import { selectedReportsFilter } from '../../redux/reducers/filterReportsSlice';
import SingleUnitSkillDialog from '../SingleUnit/SingleUnitSkillDialog';
import { Skill } from '../../models/Skill';
import { AssessmentHistoryQueryData } from './AssessmentHistoryQueryData';
import { DownloadCsvAssessmentHistoryBtn } from './DownloadCsvAssessmentHistoryBtn';
import { InputText } from 'primereact/inputtext';
import { PageInfo } from '../../models/PageInfo';
import { format } from 'date-fns';
import { useDebounce } from 'primereact/hooks';
import { selectedCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { StudentNameLink } from '../common/StudentNameLink/StudentNameLink';
import { PiiTypes } from '../../analytics/dataMasking';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { MaskedInput } from '../MaskedData/MaskedInput';

export interface SkillAssessmentTableItem {
  id: string;
  userId: string;
  attempted: number;
  skill: { id: string; name?: string | null; code?: string | null } | null;
  studentName: string;
  firstName?: string;
  lastName?: string;
  publicUserId: string;
  skillName: string;
  score?: number | string;
  completedOn: string;
}

export interface SkillAssessmentTableFilters {
  first: number;
  rows: number;
  page?: number;
  pageCount: number;
  sortField: string;
  sortOrder: 1 | -1;
  sortOrderAttempted: 1 | -1;
  sortBySkill: boolean | null;
  filters: Filters;
  multiSortMeta: null;
  prevPage: number;
}

interface Filters {
  name: string;
  classes: string[];
  roadmaps: string[] | null;
  categories: string[] | null;
  skillGrades: string[] | null;
  gradeLevels: string[] | null;
  studentName: string | null;
  skillNameCode: string | null;
  completedOn: (Date | null)[] | null;
  score: number[] | null;
  [key: string]: string | number | string[] | number[] | (Date | null)[] | { id: string; name: string }[] | null;
}

export const filterStateSkillsAssessmentsInitValue: SkillAssessmentTableFilters = {
  first: 0,
  rows: 10,
  page: 0,
  pageCount: 0,
  sortField: 'lastName',
  sortOrder: 1,
  sortOrderAttempted: -1,
  sortBySkill: false,
  filters: {
    name: '',
    classes: [],
    roadmaps: null,
    categories: null,
    skillGrades: null,
    gradeLevels: null,
    studentName: null,
    skillNameCode: null,
    completedOn: null,
    score: null,
  },
  multiSortMeta: null,
  prevPage: 0,
};

const SkillAssessmentTable = () => {
  const [skillAssessmentsFiltered, setSkillAssessmentsFiltered] = useState<SkillAssessmentTableItem[]>([]);
  const [skillClicked, setSkillClicked] = useState<Skill | null>(null);
  const [singleUnitSkillDialogVisible, setSingleUnitSkillDialogVisible] = useState<boolean>(false);
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const [stFirst, debouncedFirst, setDebouncedFirst] = useDebounce<string | null>(null, 500);
  const [stLast, debouncedLast, setDebouncedLast] = useDebounce<string | null>(null, 500);
  const [skillName, debouncedSkillName, setDebouncedSkillName] = useDebounce<string | null>(null, 500);
  const [filterState, setFilterState] = useState({
    ...filterStateSkillsAssessmentsInitValue,
    filters: {
      ...filterStateSkillsAssessmentsInitValue.filters,
      classes: reportsFilterRedux.classes.map(cl => cl.id),
      roadmaps: reportsFilterRedux.roadmaps.id ? [reportsFilterRedux.roadmaps.id] : null,
      gradeLevels: reportsFilterRedux.grades.length ? reportsFilterRedux.grades.map(gl => gl.id) : null,
      skillGrades: reportsFilterRedux.skillGrades.length ? reportsFilterRedux.skillGrades.map(sg => sg.code) : null,
    },
  });

  useEffect(() => {
    setFilterState({ ...filterStateSkillsAssessmentsInitValue, filters: filterState.filters });
  }, [filterState.filters]);

  const isCsvProcessing = useAppSelector(selectedCsvProcess);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);

  useEffect(() => {
    setFirstLoad(true);
    setFilterState({
      ...filterStateSkillsAssessmentsInitValue,
      filters: {
        ...filterState.filters,
        classes: reportsFilterRedux.classes.map(cl => cl.id),
        roadmaps: reportsFilterRedux.roadmaps.id ? [reportsFilterRedux.roadmaps.id] : null,
        skillGrades: reportsFilterRedux.skillGrades.length ? reportsFilterRedux.skillGrades.map(sg => sg.code) : null,
        gradeLevels: reportsFilterRedux.grades.length ? reportsFilterRedux.grades.map(gl => gl.id) : null,
      },
    });
  }, [reportsFilterRedux]);

  const resultFilterOptions = [
    { label: '0%', value: 0 },
    { label: '20%', value: 2 },
    { label: '40%', value: 4 },
    { label: '60%', value: 6 },
    { label: '80%', value: 8 },
    { label: '100%', value: 10 },
  ];

  const skillNameBodyTemplate = (rowData: SkillAssessmentTableItem) => {
    const handleClick = () => {
      setSkillClicked({
        id: rowData.skill?.name || '',
        code: rowData.skill?.code || '',
        name: rowData.skill?.name || '',
      });
      setSingleUnitSkillDialogVisible(true);
    };

    return <a onClick={() => handleClick()}>{`${rowData.skill?.name} (${rowData.skill?.code})`}</a>;
  };

  const dateBodyTemplate = (dateCompleted: Date) => {
    const date = new Date(dateCompleted);
    return format(date, 'Pp');
  };

  const dateCompletedRowFilterTemplate = () => {
    return (
      <Calendar
        id="dateRange"
        value={filterState.filters.completedOn}
        onChange={e => {
          setFetchingData(true);
          setFilterState({
            ...filterState,
            filters: { ...filterState.filters, completedOn: e.target.value ? e.target.value : null },
          });
        }}
        selectionMode="range"
        hideOnRangeSelection
        showIcon
        placeholder="Search"
        className="p-column-filter"
        showButtonBar
        readOnlyInput
        disabled={isCsvProcessing}
      />
    );
  };

  const rowFilterTemplate = (
    option: ColumnFilterElementTemplateOptions,
    options: { value: number; label: string }[],
  ) => (
    <MultiSelect
      disabled={!options.length || isCsvProcessing}
      name={option.field}
      optionLabel="label"
      value={filterState.filters.score}
      options={options}
      onChange={(e: MultiSelectChangeEvent) => {
        setFetchingData(true);
        setFilterState({
          ...filterState,
          filters: {
            ...filterState.filters,
            score: e.target.value.length ? e.target.value : null,
          },
        });
      }}
      placeholder="Search"
      className="p-column-filter"
      maxSelectedLabels={1}
      style={{ minWidth: '14rem' }}
    />
  );

  const getResult = (result: number) => {
    if (result >= 0 && result <= 40) {
      return 'nc_keep-learning';
    } else if (result >= 41 && result <= 60) {
      return 'nc_almost-there';
    } else if (result >= 61 && result <= 99) {
      return 'nc_passed';
    } else if (result >= 99) {
      return 'nc_perfect';
    }
  };

  const resultBodyTemplate = (result: number) => {
    return <Tag value={`${result}%`} className={getResult(result)} rounded />;
  };

  const skillTableHeader = () => {
    return (
      <div className="w-full flex justify-content-end">
        <DownloadCsvAssessmentHistoryBtn filters={{ ...filterState }} totalRecords={totalRecords} />
      </div>
    );
  };

  useEffect(() => {
    setFetchingData(true);
    const fullName = `${debouncedFirst || ''} ${debouncedLast || ''}`.trim();
    setFilterState({
      ...filterState,
      filters: { ...filterState.filters, studentName: fullName },
    });
  }, [debouncedFirst, debouncedLast]);

  useEffect(() => {
    setFetchingData(true);
    setFilterState({
      ...filterState,
      filters: { ...filterState.filters, skillNameCode: debouncedSkillName || '' },
    });
  }, [debouncedSkillName]);

  const normalizeResult = (score: number, maxScore: number) => {
    if (!score || !maxScore) return 0;
    const pct = (score / maxScore) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  };

  const studentNameLink = (rowData: SkillAssessmentTableItem, name: string | undefined, piiType: PiiTypes) => (
    <StudentNameLink studentId={rowData.userId} classIds={reportsFilterRedux.classes.map(c => c.id)}>
      <MaskedStudentData student={rowData} type={piiType}>
        {name}
      </MaskedStudentData>
    </StudentNameLink>
  );

  return (
    <>
      <Suspense>
        <AssessmentHistoryQueryData
          filterState={filterState}
          setFetchingData={setFetchingData}
          setSkillAssessments={setSkillAssessmentsFiltered}
          pageInfo={pageInfo}
          setPageInfo={setPageInfo}
          setTotalRecords={setTotalRecords}
          setFirstLoad={setFirstLoad}
          fetchingData={fetchingData}
          firstLoad={firstLoad}
        />
      </Suspense>
      <DataTable
        header={skillTableHeader}
        lazy
        loading={fetchingData}
        stripedRows
        paginator
        rows={filterState.rows}
        rowsPerPageOptions={[5, 10, 25]}
        sortField={filterState.sortField}
        totalRecords={totalRecords}
        sortOrder={filterState.sortOrder}
        filterDisplay="row"
        emptyMessage="No students found."
        value={skillAssessmentsFiltered}
        scrollable
        first={filterState.first}
        scrollHeight="600px"
        className="w-full"
        onPage={(e: DataTableStateEvent) => {
          setFetchingData(true);
          const newPage: SkillAssessmentTableFilters = {
            ...filterState,
            page: e.page,
            rows: e.rows,
            first: e.first,
            pageCount: e.pageCount!,
            prevPage: filterState.page!,
          };
          setFilterState(newPage);
        }}
        onSort={e => {
          setFetchingData(true);
          setFilterState({
            ...filterState,
            sortField: (e.sortField as string) || 'lastName',
            sortOrder: (e.sortOrder || 1) as 1 | -1,
            ...(e.sortField === 'attempted' && {
              sortOrderAttempted: filterState.sortOrderAttempted === -1 ? 1 : -1,
            }),
          });
        }}
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        <Column
          field="firstName"
          header="First Name"
          sortable
          showFilterMenu={false}
          filter
          body={(rowData: SkillAssessmentTableItem) => studentNameLink(rowData, rowData.firstName, PiiTypes.FirstName)}
          filterElement={() => (
            <MaskedInput>
              <InputText
                disabled={isCsvProcessing}
                value={stFirst || ''}
                onChange={e => setDebouncedFirst(e.target.value)}
                placeholder="Search"
              />
            </MaskedInput>
          )}
        />
        <Column
          field="lastName"
          header="Last Name"
          filter
          showFilterMenu={false}
          body={(rowData: SkillAssessmentTableItem) => studentNameLink(rowData, rowData.lastName, PiiTypes.LastName)}
          filterElement={() => (
            <MaskedInput>
              <InputText
                disabled={isCsvProcessing}
                value={stLast || ''}
                onChange={e => setDebouncedLast(e.target.value)}
                placeholder="Search"
              />
            </MaskedInput>
          )}
          sortable
        />

        <Column
          field="skillNameCode"
          header="Skill"
          filter
          filterElement={() => (
            <InputText
              disabled={isCsvProcessing}
              value={skillName ? skillName : ''}
              onChange={e => {
                setDebouncedSkillName(e.target.value);
              }}
            />
          )}
          filterPlaceholder="Search"
          showFilterMenu={false}
          body={skillNameBodyTemplate}
          className="w-2"
        ></Column>
        <Column field="attempted" sortable header="Attempt" showFilterMenu={false} className="w-2"></Column>

        <Column
          header="Date Completed"
          filter
          filterElement={dateCompletedRowFilterTemplate}
          body={rowData => dateBodyTemplate(rowData.completedOn)}
          showFilterMenu={false}
          dataType="date"
          className="w-1"
        ></Column>
        <Column
          header="Result"
          filter
          filterElement={option => rowFilterTemplate(option, resultFilterOptions)}
          body={rowData => resultBodyTemplate(normalizeResult(Number(rowData.score), rowData.max_score))}
          showFilterMenu={false}
          className="w-2"
        ></Column>
      </DataTable>

      {skillClicked && (
        <SingleUnitSkillDialog
          skill={skillClicked}
          visible={singleUnitSkillDialogVisible}
          setVisible={setSingleUnitSkillDialogVisible}
        />
      )}
    </>
  );
};

export default SkillAssessmentTable;
